/* eslint react/jsx-props-no-spreading: 0 */
import {
    Box,
    Stack,
    Divider,
    Typography,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    CircularProgress,
} from '@mui/material';
import {
    Accordion,
    Button,
    SortedSelect,
    FormStepComponentProps,
    TextField,
    ProfileType,
    backendServices,
    PasswordField,
} from 'common';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { buyerSignUpFormText } from '../BuyerSignUp.constants';
import { BuyerSignupFormSchema } from '../BuyerSignUp.schema';
import {
    DataFeed,
    DeliveryMethodInbound,
    FeedMessageType,
    ManualDataFeedFormat,
    SftpProtocol,
    sftpProtocolOptions,
} from '../../../constants';
import { useBuyerSignUpFormDefaults } from '../../../services';
import { generatePasswordFTP } from '../BuyerSignUp.utils';
import { useAutomatedFeedFormats } from '../../../services/Company/useBuyerSignUpFormDefaults';

const text = buyerSignUpFormText.inboundDataFeeds;

type InboundDataFeedsProps = FormStepComponentProps<typeof BuyerSignupFormSchema>;

export const InboundDataFeeds = ({ control }: InboundDataFeedsProps) => {
    // react-hook-form hooks
    const { setValue, getValues, clearErrors } = useFormContext(); // component wrapped with FormProvider in MultiStepForm.tsx

    const { viewModelDefault } = useBuyerSignUpFormDefaults();
    const feedFormatOptions = useAutomatedFeedFormats(viewModelDefault!);

    function determineInitialSelectedDeliveryMethod(): string {
        if (getValues('inboundDataFeeds.deliveryMethod.useBuyerSftp')) {
            return DeliveryMethodInbound.Buyer;
        }
        if (getValues('inboundDataFeeds.deliveryMethod.useEmail')) {
            return DeliveryMethodInbound.Email;
        }
        return DeliveryMethodInbound.Transcepta;
    }

    function shouldDataFormatSelectionShow(dataFormat: string): boolean {
        return dataFormat !== ManualDataFeedFormat;
    }

    // use form to determine state upon load
    const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<string>(
        determineInitialSelectedDeliveryMethod()
    );

    const [useInfinxIntegration, setUseInfinxIntegration] = useState<boolean>(
        getValues('inboundDataFeeds.infinxIntegration.useInfinxIntegration')
    );
    const [useExistingInfinxConnection, setUseExistingInfinxConnection] = useState<boolean>(
        getValues('inboundDataFeeds.infinxIntegration.useExistingInfinxConnection')
    );
    const [infinxSearchLoading, setInfinxSearchLoading] = useState(false);
    const [infinxSearchError, setInfinxSearchError] = useState(false);

    const [useAutomatedVendorFeed, setUseAutomatedVendorFeed] = useState<boolean>(
        shouldDataFormatSelectionShow(getValues('inboundDataFeeds.vendorFeedFormat'))
    );
    const [useAutomatedUserFeed, setUseAutomatedUserFeed] = useState<boolean>(
        shouldDataFormatSelectionShow(getValues('inboundDataFeeds.userFeedFormat'))
    );
    const [useAutomatedGLAccountFeed, setUseAutomatedGLAccountFeed] = useState<boolean>(
        shouldDataFormatSelectionShow(getValues('inboundDataFeeds.glAccountFeedFormat'))
    );
    const [useAutomatedPOValidationFeed, setUseAutomatedPOValidationFeed] = useState<boolean>(
        shouldDataFormatSelectionShow(getValues('inboundDataFeeds.poValidationFeedFormat'))
    );
    const [useAutomatedPODeliveryFeed, setUseAutomatedPODeliveryFeed] = useState<boolean>(
        shouldDataFormatSelectionShow(getValues('inboundDataFeeds.poDeliveryFeedFormat'))
    );

    const transceptaSftp =
        useBuyerSignUpFormDefaults()!.formDefaults!.inboundDataFeeds!.deliveryMethod!.sftpConnection!;
    const apiFTPDownload = new backendServices.Apis.FTPDownloadApi();

    function clearDeliveryMethodData(useDefaults: boolean = false) {
        clearErrors('inboundDataFeeds.deliveryMethod.sftpConnection');
        clearErrors('inboundDataFeeds.deliveryMethod.emailAddress');

        if (useDefaults) {
            setValue(
                'inboundDataFeeds.deliveryMethod.sftpConnection',
                {
                    server: transceptaSftp.server,
                    user: '',
                    password: '',
                    downloadFolder: transceptaSftp.downloadFolder,
                    uploadFolder: transceptaSftp.uploadFolder,
                    protocol: transceptaSftp.protocol,
                    port: transceptaSftp.port,
                    timeoutInSeconds: transceptaSftp.timeoutInSeconds?.toString() ?? '0',
                },
                { shouldDirty: false, shouldValidate: false }
            );
        } else {
            setValue(
                'inboundDataFeeds.deliveryMethod.sftpConnection',
                {
                    server: '',
                    user: '',
                    password: '',
                    downloadFolder: '',
                    uploadFolder: '',
                    protocol: SftpProtocol.SFTP,
                    port: '',
                    timeoutInSeconds: '',
                },
                { shouldDirty: false, shouldValidate: false }
            );
        }
        setValue('inboundDataFeeds.deliveryMethod.emailAddress', '', { shouldDirty: false, shouldValidate: false });
    }

    function handleSelectDeliveryMethod(val: string) {
        switch (val) {
            case DeliveryMethodInbound.Transcepta:
                clearDeliveryMethodData(true);

                setValue('inboundDataFeeds.deliveryMethod.useTransceptaSftp', true);
                setValue('inboundDataFeeds.deliveryMethod.useBuyerSftp', false);
                setValue('inboundDataFeeds.deliveryMethod.useEmail', false);

                break;

            case DeliveryMethodInbound.Buyer:
                clearDeliveryMethodData(false);

                setValue('inboundDataFeeds.deliveryMethod.useTransceptaSftp', false);
                setValue('inboundDataFeeds.deliveryMethod.useBuyerSftp', true);
                setValue('inboundDataFeeds.deliveryMethod.useEmail', false);

                break;

            case DeliveryMethodInbound.Email:
                clearDeliveryMethodData(false);

                setValue('inboundDataFeeds.deliveryMethod.useTransceptaSftp', false);
                setValue('inboundDataFeeds.deliveryMethod.buyer', false);
                setValue('inboundDataFeeds.deliveryMethod.useEmail', true);

                break;

            default:
                break;
        }

        setSelectedDeliveryMethod(val);
    }

    function handleSelectExistingInfinxConnection(val: boolean) {
        setInfinxSearchError(false);
        setUseExistingInfinxConnection(val);
        setValue('inboundDataFeeds.infinxIntegration.useExistingInfinxConnection', val);

        // clear existing connection data
        setValue('inboundDataFeeds.infinxIntegration.invoiceProfileId', '', {
            shouldDirty: false,
            shouldValidate: false,
        });
        setValue(
            'inboundDataFeeds.infinxIntegration.sftpConnection',
            {
                server: '',
                user: '',
                password: '',
                downloadFolder: '',
                uploadFolder: '',
                protocol: SftpProtocol.SFTP,
                port: '',
                timeoutInSeconds: '',
            },
            { shouldDirty: false, shouldValidate: false }
        );

        clearErrors('inboundDataFeeds.infinxIntegration.sftpConnection');
    }

    function handleSelectInfinxIntegration(val: boolean) {
        setUseInfinxIntegration(val);
        setUseExistingInfinxConnection(false);

        setValue('inboundDataFeeds.infinxIntegration.useInfinxIntegration', val);
        handleSelectExistingInfinxConnection(false);
    }

    async function handleSearchForInfinxConnection() {
        clearErrors('inboundDataFeeds.infinxIntegration');
        setInfinxSearchLoading(true);
        setInfinxSearchError(false);

        try {
            const res = await apiFTPDownload.getFTPDownloadByProfileIdAndType({
                profileId: getValues('inboundDataFeeds.infinxIntegration.invoiceProfileId'),
                profileType: ProfileType.Invoice,
            });
            const connection = res.data;
            setValue(
                'inboundDataFeeds.infinxIntegration.sftpConnection',
                {
                    server: connection.Server,
                    user: connection.User,
                    password: connection.Password,
                    downloadFolder: connection.DownloadFolder,
                    uploadFolder: connection.UploadFolder,
                    protocol: connection.Protocol,
                    port: connection.Port!.toString(),
                    timeoutInSeconds: connection.Timeout!.toString(),
                },
                { shouldDirty: true }
            );
        } catch {
            setInfinxSearchError(true);
        } finally {
            setInfinxSearchLoading(false);
        }
    }

    function handleGenerateTransceptaFTPPassword() {
        const password = generatePasswordFTP();

        clearErrors('inboundDataFeeds.deliveryMethod.sftpConnection.password');
        setValue('inboundDataFeeds.deliveryMethod.sftpConnection.password', password, { shouldDirty: true });
    }

    function handleSelectFeedFormat(isAutomated: boolean, feed: DataFeed) {
        switch (feed) {
            case DataFeed.Vendor:
                setUseAutomatedVendorFeed(isAutomated);
                break;
            case DataFeed.User:
                setUseAutomatedUserFeed(isAutomated);
                break;
            case DataFeed.GLAccount:
                setUseAutomatedGLAccountFeed(isAutomated);
                break;
            case DataFeed.POValidation:
                setUseAutomatedPOValidationFeed(isAutomated);
                break;
            case DataFeed.PODelivery:
                setUseAutomatedPODeliveryFeed(isAutomated);
                break;
            default:
                break;
        }

        if (isAutomated) {
            setValue(`inboundDataFeeds.${feed}`, '', { shouldDirty: true });
        } else {
            clearErrors(`inboundDataFeeds.${feed}`);
            setValue(`inboundDataFeeds.${feed}`, ManualDataFeedFormat, { shouldDirty: true });
        }
    }

    const deliveryMethodSection = (
        <Box width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.deliveryMethod.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <FormControl fullWidth>
                            <RadioGroup
                                value={selectedDeliveryMethod}
                                onChange={(e) => handleSelectDeliveryMethod(e.target.value)}
                            >
                                <FormControlLabel
                                    value={DeliveryMethodInbound.Transcepta}
                                    control={<Radio data-testid="select-deliveryMethod-radio-transcepta" />}
                                    label="Transcepta SFTP"
                                />
                                <FormControlLabel
                                    value={DeliveryMethodInbound.Buyer}
                                    control={<Radio data-testid="select-deliveryMethod-radio-buyer" />}
                                    label="Buyer SFTP"
                                />
                                <FormControlLabel
                                    value={DeliveryMethodInbound.Email}
                                    control={<Radio data-testid="select-deliveryMethod-radio-email" />}
                                    label="Email"
                                />
                            </RadioGroup>
                        </FormControl>

                        <Stack
                            direction="column"
                            justifyContent="flex-start"
                            alignItems="flex-start"
                            spacing={2}
                            width="100%"
                        >
                            {selectedDeliveryMethod !== DeliveryMethodInbound.Email && (
                                <>
                                    <Controller
                                        name="inboundDataFeeds.deliveryMethod.sftpConnection.server"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                label="Server"
                                                testId="enter-deliveryMethod-sftpConnection-server"
                                                {...field}
                                                error={fieldState.invalid}
                                                helperText={fieldState.error?.message}
                                                required
                                                disabled={selectedDeliveryMethod === DeliveryMethodInbound.Transcepta}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="inboundDataFeeds.deliveryMethod.sftpConnection.user"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                label="User"
                                                testId="enter-deliveryMethod-sftpConnection-user"
                                                {...field}
                                                error={fieldState.invalid}
                                                helperText={fieldState.error?.message}
                                                required
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="inboundDataFeeds.deliveryMethod.sftpConnection.password"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <Stack
                                                direction="row"
                                                justifyContent="flex-start"
                                                alignItems="center"
                                                width="100%"
                                            >
                                                <PasswordField
                                                    label="Password"
                                                    testId="enter-deliveryMethod-sftpConnection-password"
                                                    {...field}
                                                    error={fieldState.invalid}
                                                    helperText={fieldState.error?.message}
                                                    required
                                                />
                                                {selectedDeliveryMethod === DeliveryMethodInbound.Transcepta && (
                                                    <Button onClick={handleGenerateTransceptaFTPPassword}>
                                                        Generate
                                                    </Button>
                                                )}
                                            </Stack>
                                        )}
                                    />
                                    <Controller
                                        name="inboundDataFeeds.deliveryMethod.sftpConnection.downloadFolder"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                label="Download folder"
                                                testId="enter-deliveryMethod-sftpConnection-downloadFolder"
                                                {...field}
                                                error={fieldState.invalid}
                                                helperText={fieldState.error?.message}
                                                required
                                                disabled={selectedDeliveryMethod === DeliveryMethodInbound.Transcepta}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="inboundDataFeeds.deliveryMethod.sftpConnection.uploadFolder"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                label="Upload folder"
                                                testId="enter-deliveryMethod-sftpConnection-uploadFolder"
                                                {...field}
                                                error={fieldState.invalid}
                                                helperText={fieldState.error?.message}
                                                required
                                                disabled={selectedDeliveryMethod === DeliveryMethodInbound.Transcepta}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="inboundDataFeeds.deliveryMethod.sftpConnection.protocol"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <SortedSelect
                                                id="Protocol"
                                                label="Protocol"
                                                testId="enter-deliveryMethod-sftpConnection-protocol"
                                                options={sftpProtocolOptions}
                                                error={fieldState.invalid}
                                                helperText={fieldState.error?.message}
                                                value={field.value ?? null}
                                                onChange={field.onChange}
                                                required
                                                disabled={selectedDeliveryMethod === DeliveryMethodInbound.Transcepta}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="inboundDataFeeds.deliveryMethod.sftpConnection.port"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                label="Port"
                                                testId="enter-deliveryMethod-sftpConnection-port"
                                                {...field}
                                                error={fieldState.invalid}
                                                helperText={fieldState.error?.message}
                                                required
                                                disabled={selectedDeliveryMethod === DeliveryMethodInbound.Transcepta}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="inboundDataFeeds.deliveryMethod.sftpConnection.timeoutInSeconds"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            //@ts-ignore
                                            <TextField
                                                label="Timeout (in seconds)"
                                                testId="enter-deliveryMethod-sftpConnection-timeoutInSeconds"
                                                {...field}
                                                error={fieldState.invalid}
                                                helperText={fieldState.error?.message}
                                                required
                                                numericOnly
                                                disabled={selectedDeliveryMethod === DeliveryMethodInbound.Transcepta}
                                            />
                                        )}
                                    />
                                </>
                            )}
                        </Stack>
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    const infinxIntegrationSection = (
        <Box width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.infinxIntegration.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <Controller
                            name="inboundDataFeeds.infinxIntegration.useInfinxIntegration"
                            control={control}
                            render={({ fieldState }) => (
                                <>
                                    <FormControl fullWidth error={fieldState.invalid}>
                                        <RadioGroup
                                            value={useInfinxIntegration}
                                            onChange={(e) => handleSelectInfinxIntegration(JSON.parse(e.target.value))}
                                        >
                                            <FormControlLabel
                                                value={true}
                                                control={<Radio data-testid="select-infinxIntegration-radio-yes" />}
                                                label="Yes"
                                            />
                                            <FormControlLabel
                                                value={false}
                                                control={<Radio data-testid="select-infinxIntegration-radio-no" />}
                                                label="No"
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                    {useInfinxIntegration && (
                                        <FormControl fullWidth error={fieldState.invalid}>
                                            {/* TODO: use true form label? */}
                                            <Typography>{text.sections.infinxExistingConnection.header}</Typography>
                                            <RadioGroup
                                                value={useExistingInfinxConnection}
                                                onChange={(e) =>
                                                    handleSelectExistingInfinxConnection(JSON.parse(e.target.value))
                                                }
                                            >
                                                <Stack
                                                    direction="row"
                                                    justifyContent="flex-start"
                                                    alignItems="center"
                                                    width="100%"
                                                >
                                                    <FormControlLabel
                                                        value={true}
                                                        control={
                                                            <Radio data-testid="select-infinxIntegration-useExisting-radio-yes" />
                                                        }
                                                        label="Yes"
                                                        sx={{ width: '40%' }}
                                                    />
                                                    {useExistingInfinxConnection && (
                                                        <Controller
                                                            name="inboundDataFeeds.infinxIntegration.invoiceProfileId"
                                                            control={control}
                                                            render={({ field, fieldState: subFieldState }) => (
                                                                <Stack
                                                                    direction="row"
                                                                    justifyContent="flex-start"
                                                                    alignItems="center"
                                                                    width="100%"
                                                                >
                                                                    <TextField
                                                                        label="Invoice profile ID"
                                                                        testId="enter-infinxIntegration-invoiceProfileId"
                                                                        {...field}
                                                                        error={
                                                                            subFieldState.invalid || infinxSearchError
                                                                        }
                                                                        helperText={
                                                                            infinxSearchError
                                                                                ? 'Connection not found'
                                                                                : subFieldState.error?.message
                                                                        }
                                                                        required
                                                                    />
                                                                    <Button
                                                                        onClick={handleSearchForInfinxConnection}
                                                                        disabled={
                                                                            infinxSearchLoading ||
                                                                            !getValues(
                                                                                'inboundDataFeeds.infinxIntegration.invoiceProfileId'
                                                                            )
                                                                        }
                                                                    >
                                                                        {infinxSearchLoading ? (
                                                                            <CircularProgress size="20px" />
                                                                        ) : (
                                                                            'Search'
                                                                        )}
                                                                    </Button>
                                                                </Stack>
                                                            )}
                                                        />
                                                    )}
                                                </Stack>
                                                <FormControlLabel
                                                    value={false}
                                                    control={
                                                        <Radio data-testid="select-infinxIntegration-useExisting-radio-no" />
                                                    }
                                                    label="No"
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    )}
                                    {useInfinxIntegration && (
                                        <Stack
                                            direction="column"
                                            justifyContent="flex-start"
                                            alignItems="flex-start"
                                            spacing={2}
                                            width="100%"
                                        >
                                            <Controller
                                                name="inboundDataFeeds.infinxIntegration.sftpConnection.server"
                                                control={control}
                                                render={({ field, fieldState: subFieldState }) => (
                                                    <TextField
                                                        label="Server"
                                                        testId="enter-infinxIntegration-sftpConnection-server"
                                                        {...field}
                                                        error={subFieldState.invalid}
                                                        helperText={subFieldState.error?.message}
                                                        required
                                                        disabled={useExistingInfinxConnection}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="inboundDataFeeds.infinxIntegration.sftpConnection.user"
                                                control={control}
                                                render={({ field, fieldState: subFieldState }) => (
                                                    <TextField
                                                        label="User"
                                                        testId="enter-infinxIntegration-sftpConnection-user"
                                                        {...field}
                                                        error={subFieldState.invalid}
                                                        helperText={subFieldState.error?.message}
                                                        required
                                                        disabled={useExistingInfinxConnection}
                                                    />
                                                )}
                                            />
                                            {!useExistingInfinxConnection && (
                                                <Controller
                                                    name="inboundDataFeeds.infinxIntegration.sftpConnection.password"
                                                    control={control}
                                                    render={({ field, fieldState: subFieldState }) => (
                                                        <PasswordField
                                                            label="Password"
                                                            testId="enter-infinxIntegration-sftpConnection-password"
                                                            {...field}
                                                            error={subFieldState.invalid}
                                                            helperText={subFieldState.error?.message}
                                                            required
                                                        />
                                                    )}
                                                />
                                            )}
                                            <Controller
                                                name="inboundDataFeeds.infinxIntegration.sftpConnection.downloadFolder"
                                                control={control}
                                                render={({ field, fieldState: subFieldState }) => (
                                                    <TextField
                                                        label="Download folder"
                                                        testId="enter-infinxIntegration-sftpConnection-downloadFolder"
                                                        {...field}
                                                        error={subFieldState.invalid}
                                                        helperText={subFieldState.error?.message}
                                                        required
                                                        disabled={useExistingInfinxConnection}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="inboundDataFeeds.infinxIntegration.sftpConnection.uploadFolder"
                                                control={control}
                                                render={({ field, fieldState: subFieldState }) => (
                                                    <TextField
                                                        label="Upload folder"
                                                        testId="enter-infinxIntegration-sftpConnection-uploadFolder"
                                                        {...field}
                                                        error={subFieldState.invalid}
                                                        helperText={subFieldState.error?.message}
                                                        required
                                                        disabled={useExistingInfinxConnection}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="inboundDataFeeds.infinxIntegration.sftpConnection.protocol"
                                                control={control}
                                                render={({ field, fieldState: subFieldState }) => (
                                                    <SortedSelect
                                                        id="Protocol"
                                                        label="Protocol"
                                                        testId="enter-infinxIntegration-sftpConnection-protocol"
                                                        options={sftpProtocolOptions}
                                                        error={subFieldState.invalid}
                                                        helperText={subFieldState.error?.message}
                                                        value={field.value ?? null}
                                                        onChange={field.onChange}
                                                        required
                                                        disabled={useExistingInfinxConnection}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="inboundDataFeeds.infinxIntegration.sftpConnection.port"
                                                control={control}
                                                render={({ field, fieldState: subFieldState }) => (
                                                    <TextField
                                                        label="Port"
                                                        testId="enter-infinxIntegration-sftpConnection-port"
                                                        {...field}
                                                        error={subFieldState.invalid}
                                                        helperText={subFieldState.error?.message}
                                                        required
                                                        disabled={useExistingInfinxConnection}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="inboundDataFeeds.infinxIntegration.sftpConnection.timeoutInSeconds"
                                                control={control}
                                                render={({ field, fieldState: subFieldState }) => (
                                                    //@ts-ignore
                                                    <TextField
                                                        label="Timeout (in seconds)"
                                                        testId="enter-infinxIntegration-sftpConnection-timeoutInSeconds"
                                                        {...field}
                                                        error={subFieldState.invalid}
                                                        helperText={subFieldState.error?.message}
                                                        required
                                                        numericOnly
                                                        disabled={useExistingInfinxConnection}
                                                    />
                                                )}
                                            />
                                        </Stack>
                                    )}
                                </>
                            )}
                        />
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    const vendorFeedFormatSection = (
        <Box width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.vendorFeedFormat.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <Controller
                            name="inboundDataFeeds.vendorFeedFormat"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormControl fullWidth error={fieldState.invalid}>
                                    <RadioGroup
                                        value={useAutomatedVendorFeed}
                                        onChange={(e) =>
                                            handleSelectFeedFormat(JSON.parse(e.target.value), DataFeed.Vendor)
                                        }
                                    >
                                        <Stack
                                            direction="row"
                                            justifyContent="flex-start"
                                            alignItems="center"
                                            width="100%"
                                        >
                                            <FormControlLabel
                                                value={true}
                                                control={<Radio data-testid="select-feed-vendor-radio-automated" />}
                                                label="Yes"
                                                sx={{ width: '40%' }}
                                            />
                                            {useAutomatedVendorFeed && (
                                                <SortedSelect
                                                    id="Data Format Vendor"
                                                    label="Data Format"
                                                    testId="select-feed-vendor-select-format"
                                                    options={feedFormatOptions[FeedMessageType.Vendor]}
                                                    error={fieldState.invalid}
                                                    helperText={fieldState.error?.message}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    required
                                                />
                                            )}
                                        </Stack>
                                        <FormControlLabel
                                            value={false}
                                            control={<Radio data-testid="select-feed-vendor-radio-manual" />}
                                            label="No"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            )}
                        />
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    const userFeedFormatSection = (
        <Box width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.userFeedFormat.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <Controller
                            name="inboundDataFeeds.userFeedFormat"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormControl fullWidth error={fieldState.invalid}>
                                    <RadioGroup
                                        value={useAutomatedUserFeed}
                                        onChange={(e) =>
                                            handleSelectFeedFormat(JSON.parse(e.target.value), DataFeed.User)
                                        }
                                    >
                                        <Stack
                                            direction="row"
                                            justifyContent="flex-start"
                                            alignItems="center"
                                            width="100%"
                                        >
                                            <FormControlLabel
                                                value={true}
                                                control={<Radio data-testid="select-feed-user-radio-automated" />}
                                                label="Yes"
                                                sx={{ width: '40%' }}
                                            />
                                            {useAutomatedUserFeed && (
                                                <SortedSelect
                                                    id="Data Format user"
                                                    label="Data Format"
                                                    testId="select-feed-user-select-format"
                                                    options={feedFormatOptions[FeedMessageType.User]}
                                                    error={fieldState.invalid}
                                                    helperText={fieldState.error?.message}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    required
                                                />
                                            )}
                                        </Stack>
                                        <FormControlLabel
                                            value={false}
                                            control={<Radio data-testid="select-feed-user-radio-manual" />}
                                            label="No"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            )}
                        />
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    const glAccountFeedFormatSection = (
        <Box width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.glAccountFeedFormat.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <Controller
                            name="inboundDataFeeds.glAccountFeedFormat"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormControl fullWidth error={fieldState.invalid}>
                                    <RadioGroup
                                        value={useAutomatedGLAccountFeed}
                                        onChange={(e) =>
                                            handleSelectFeedFormat(JSON.parse(e.target.value), DataFeed.GLAccount)
                                        }
                                    >
                                        <Stack
                                            direction="row"
                                            justifyContent="flex-start"
                                            alignItems="center"
                                            width="100%"
                                        >
                                            <FormControlLabel
                                                value={true}
                                                control={<Radio data-testid="select-feed-glAccount-radio-automated" />}
                                                label="Yes"
                                                sx={{ width: '40%' }}
                                            />
                                            {useAutomatedGLAccountFeed && (
                                                <SortedSelect
                                                    id="Data Format glAccount"
                                                    label="Data Format"
                                                    testId="select-feed-glAccount-select-format"
                                                    options={feedFormatOptions[FeedMessageType.GLAccount]}
                                                    error={fieldState.invalid}
                                                    helperText={fieldState.error?.message}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    required
                                                />
                                            )}
                                        </Stack>
                                        <FormControlLabel
                                            value={false}
                                            control={<Radio data-testid="select-feed-glAccount-radio-manual" />}
                                            label="No"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            )}
                        />
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    const poValidationFeedFormatSection = (
        <Box width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.poValidationFeedFormat.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <Controller
                            name="inboundDataFeeds.poValidationFeedFormat"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormControl fullWidth error={fieldState.invalid}>
                                    <RadioGroup
                                        value={useAutomatedPOValidationFeed}
                                        onChange={(e) =>
                                            handleSelectFeedFormat(JSON.parse(e.target.value), DataFeed.POValidation)
                                        }
                                    >
                                        <Stack
                                            direction="row"
                                            justifyContent="flex-start"
                                            alignItems="center"
                                            width="100%"
                                        >
                                            <FormControlLabel
                                                value={true}
                                                control={
                                                    <Radio
                                                        data-testid="select-feed-po-validation-radio-automated"
                                                        disabled
                                                    />
                                                }
                                                label="Yes"
                                                sx={{ width: '40%' }}
                                            />
                                            {useAutomatedPOValidationFeed && (
                                                <SortedSelect
                                                    id="Data Format po"
                                                    label="Data Format"
                                                    testId="select-feed-po-validation-select-format"
                                                    options={feedFormatOptions[FeedMessageType.POUpload]}
                                                    error={fieldState.invalid}
                                                    helperText={fieldState.error?.message}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    required
                                                />
                                            )}
                                        </Stack>
                                    </RadioGroup>
                                </FormControl>
                            )}
                        />
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    const poDeliveryFeedFormatSection = (
        <Box width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.poDeliveryFeedFormat.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <Controller
                            name="inboundDataFeeds.poDeliveryFeedFormat"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormControl fullWidth error={fieldState.invalid}>
                                    <RadioGroup
                                        value={useAutomatedPODeliveryFeed}
                                        onChange={(e) =>
                                            handleSelectFeedFormat(JSON.parse(e.target.value), DataFeed.PODelivery)
                                        }
                                    >
                                        <Stack
                                            direction="row"
                                            justifyContent="flex-start"
                                            alignItems="center"
                                            width="100%"
                                        >
                                            <FormControlLabel
                                                value={true}
                                                control={
                                                    <Radio data-testid="select-feed-po-delivery-radio-automated" />
                                                }
                                                label="Yes"
                                                sx={{ width: '40%' }}
                                            />
                                            {useAutomatedPODeliveryFeed && (
                                                <SortedSelect
                                                    id="Data Format po"
                                                    label="Data Format"
                                                    testId="select-feed-po-delivery-select-format"
                                                    options={feedFormatOptions[FeedMessageType.PODelivery]}
                                                    error={fieldState.invalid}
                                                    helperText={fieldState.error?.message}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    required
                                                />
                                            )}
                                        </Stack>
                                        <FormControlLabel
                                            value={false}
                                            control={<Radio data-testid="select-feed-po-delivery-radio-manual" />}
                                            label="No"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            )}
                        />
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    return (
        <>
            <Box className="InboundDataFeeds" width="100%">
                <Stack
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    spacing={4}
                    divider={<Divider orientation="horizontal" flexItem />}
                >
                    <Typography variant="h1" padding={2}>
                        {text.topHeader}
                    </Typography>
                    {deliveryMethodSection}
                    {infinxIntegrationSection}
                    {vendorFeedFormatSection}
                    {userFeedFormatSection}
                    {glAccountFeedFormatSection}
                    {poValidationFeedFormatSection}
                    {poDeliveryFeedFormatSection}
                </Stack>
            </Box>
        </>
    );
};
